import * as THREE from 'three';
import type { BokehPass }    from 'three/examples/jsm/postprocessing/BokehPass';
import type { GTAOPass }     from 'three/examples/jsm/postprocessing/GTAOPass';
import type { TAARenderPass} from 'three/examples/jsm/postprocessing/TAARenderPass';
import type { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

export interface DebugGuiRefs {
  renderer:   THREE.WebGLRenderer;
  scene:      THREE.Scene;
  camera:     THREE.PerspectiveCamera;
  controls:   OrbitControls;
  bokehPass:  BokehPass;
  gtaoPass:   GTAOPass;
  taaPass:    TAARenderPass;
  getAAMode:  () => 'off' | 'smaa' | 'tsaa';
  setAAMode:  (mode: 'off' | 'smaa' | 'tsaa') => void;
}

// ─── Styles (injected once) ─────────────────────────────────────────────────

let _stylesInjected = false;

function injectStyles(): void {
  if (_stylesInjected) return;
  _stylesInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    .dbg-panel {
      position: absolute;
      top: 8px;
      left: 8px;
      z-index: 200;
      font: 11px/1.4 'Consolas', 'Monaco', monospace;
      color: #e0e0e0;
      background: rgba(8, 8, 12, 0.90);
      border: 1px solid rgba(255,255,255,0.10);
      border-radius: 6px;
      width: 260px;
      max-height: calc(100% - 16px);
      overflow-y: auto;
      overflow-x: hidden;
      user-select: none;
      box-shadow: 0 6px 24px rgba(0,0,0,0.6);
      scrollbar-width: thin;
      scrollbar-color: rgba(255,255,255,0.12) transparent;
    }
    .dbg-panel::-webkit-scrollbar { width: 4px; }
    .dbg-panel::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.14); border-radius: 2px; }

    .dbg-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 7px 10px;
      background: rgba(120,160,255,0.10);
      cursor: grab;
      border-radius: 5px 5px 0 0;
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.05em;
      color: #c8d8ff;
      border-bottom: 1px solid rgba(255,255,255,0.07);
    }
    .dbg-header:active { cursor: grabbing; }
    .dbg-collapse-btn {
      background: none;
      border: none;
      color: #c8d8ff;
      cursor: pointer;
      font-size: 12px;
      padding: 0 2px;
      line-height: 1;
    }
    .dbg-body { padding: 2px 0 6px; }
    .dbg-panel--collapsed .dbg-body { display: none; }

    .dbg-section {
      border-bottom: 1px solid rgba(255,255,255,0.05);
    }
    .dbg-section-head {
      display: flex;
      align-items: center;
      gap: 5px;
      padding: 5px 10px 4px;
      cursor: pointer;
      font-size: 9px;
      font-weight: 700;
      letter-spacing: 0.10em;
      text-transform: uppercase;
      color: #8899bb;
    }
    .dbg-section-head:hover { color: #cce; background: rgba(255,255,255,0.04); }
    .dbg-arrow { display: inline-block; transition: transform 0.12s; font-size: 8px; }
    .dbg-section--collapsed .dbg-arrow { transform: rotate(-90deg); }
    .dbg-section-body { padding: 2px 0 4px; }
    .dbg-section--collapsed .dbg-section-body { display: none; }

    .dbg-row {
      display: grid;
      grid-template-columns: 78px 1fr 44px;
      align-items: center;
      gap: 6px;
      padding: 3px 10px;
    }
    .dbg-label {
      color: #7788aa;
      font-size: 10px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .dbg-val {
      color: #7cf;
      text-align: right;
      font-size: 10px;
      font-feature-settings: 'tnum';
    }
    .dbg-slider {
      width: 100%;
      height: 3px;
      accent-color: #7cf;
      cursor: pointer;
    }
    .dbg-select-row {
      display: grid;
      grid-template-columns: 78px 1fr;
      align-items: center;
      gap: 6px;
      padding: 3px 10px;
    }
    .dbg-select {
      width: 100%;
      background: rgba(255,255,255,0.07);
      border: 1px solid rgba(255,255,255,0.12);
      border-radius: 3px;
      color: #ccd;
      font-size: 10px;
      font-family: inherit;
      padding: 2px 4px;
      cursor: pointer;
    }
    .dbg-info-row {
      display: grid;
      grid-template-columns: 78px 1fr;
      gap: 6px;
      padding: 2px 10px;
      font-size: 10px;
    }
    .dbg-info-label { color: #7788aa; }
    .dbg-info-val {
      color: #fca;
      font-feature-settings: 'tnum';
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .dbg-note {
      padding: 1px 10px 4px;
      font-size: 9px;
      color: #556;
      line-height: 1.5;
    }
    .dbg-separator {
      border: none;
      border-top: 1px solid rgba(255,255,255,0.06);
      margin: 2px 10px;
    }
  `;
  document.head.appendChild(style);
}

// ─── Primitive builders ─────────────────────────────────────────────────────

function makeSlider(
  label: string,
  min: number, max: number, step: number,
  initial: number,
  onChange: (v: number) => void,
): HTMLElement {
  const row = document.createElement('div');
  row.className = 'dbg-row';

  const lbl = document.createElement('span');
  lbl.className = 'dbg-label';
  lbl.title = label;
  lbl.textContent = label;

  const slider = document.createElement('input');
  slider.type = 'range';
  slider.className = 'dbg-slider';
  slider.min  = String(min);
  slider.max  = String(max);
  slider.step = String(step);
  slider.value = String(Math.min(max, Math.max(min, initial)));

  const decimals = step < 0.01 ? 4 : step < 0.1 ? 3 : step < 1 ? 2 : step < 10 ? 1 : 0;
  const fmt = (v: number): string => v.toFixed(decimals);

  const valSpan = document.createElement('span');
  valSpan.className = 'dbg-val';
  valSpan.textContent = fmt(initial);

  slider.addEventListener('input', () => {
    const v = parseFloat(slider.value);
    valSpan.textContent = fmt(v);
    onChange(v);
  });

  row.appendChild(lbl);
  row.appendChild(slider);
  row.appendChild(valSpan);
  return row;
}

function makeSelect(
  label: string,
  options: Array<{ label: string; value: string | number }>,
  current: string | number,
  onChange: (v: string) => void,
): HTMLElement {
  const row = document.createElement('div');
  row.className = 'dbg-select-row';

  const lbl = document.createElement('span');
  lbl.className = 'dbg-label';
  lbl.textContent = label;

  const sel = document.createElement('select');
  sel.className = 'dbg-select';
  options.forEach(({ label: l, value: v }) => {
    const opt = document.createElement('option');
    opt.value    = String(v);
    opt.textContent = l;
    if (String(v) === String(current)) opt.selected = true;
    sel.appendChild(opt);
  });
  sel.addEventListener('change', () => onChange(sel.value));

  row.appendChild(lbl);
  row.appendChild(sel);
  return row;
}

function makeInfoRow(
  label: string,
): { el: HTMLElement; setValue: (v: string) => void } {
  const row = document.createElement('div');
  row.className = 'dbg-info-row';

  const lbl = document.createElement('span');
  lbl.className = 'dbg-info-label';
  lbl.textContent = label;

  const val = document.createElement('span');
  val.className = 'dbg-info-val';
  val.textContent = '—';

  row.appendChild(lbl);
  row.appendChild(val);
  return { el: row, setValue: (v) => { val.textContent = v; } };
}

function makeSection(title: string, children: HTMLElement[]): HTMLElement {
  const section = document.createElement('div');
  section.className = 'dbg-section';

  const head = document.createElement('div');
  head.className = 'dbg-section-head';

  const arrow = document.createElement('span');
  arrow.className = 'dbg-arrow';
  arrow.textContent = '▾';
  arrow.setAttribute('aria-hidden', 'true');

  head.appendChild(arrow);
  head.appendChild(document.createTextNode(' ' + title));
  section.appendChild(head);

  const body = document.createElement('div');
  body.className = 'dbg-section-body';
  children.forEach((c) => body.appendChild(c));
  section.appendChild(body);

  head.addEventListener('click', () => section.classList.toggle('dbg-section--collapsed'));

  return section;
}

function makeSeparator(): HTMLElement {
  const hr = document.createElement('hr');
  hr.className = 'dbg-separator';
  return hr;
}

function makeNote(text: string): HTMLElement {
  const el = document.createElement('div');
  el.className = 'dbg-note';
  el.textContent = text;
  return el;
}

// ─── Drag support ───────────────────────────────────────────────────────────

function makeDraggable(panel: HTMLElement, handle: HTMLElement): void {
  let dragging = false;
  let startX = 0, startY = 0, origL = 0, origT = 0;

  handle.addEventListener('mousedown', (e) => {
    if ((e.target as HTMLElement).tagName === 'BUTTON') return;
    dragging = true;
    startX = e.clientX;
    startY = e.clientY;
    origL = parseInt(panel.style.left || '8', 10);
    origT = parseInt(panel.style.top  || '8', 10);
    e.preventDefault();
  });

  document.addEventListener('mousemove', (e) => {
    if (!dragging) return;
    panel.style.left = `${origL + e.clientX - startX}px`;
    panel.style.top  = `${origT + e.clientY - startY}px`;
  });

  document.addEventListener('mouseup', () => { dragging = false; });
}

// ─── Main factory ───────────────────────────────────────────────────────────

export function createDebugGui(
  container: HTMLElement,
  refs: DebugGuiRefs,
): { element: HTMLElement; update: () => void } {
  injectStyles();

  const { renderer, scene, camera, controls, bokehPass, gtaoPass, taaPass, getAAMode, setAAMode } = refs;

  // Typed accessor for BokehPass uniforms (Three.js does not export the type)
  type BU = Record<string, { value: number }>;
  const bokehU = (): BU => (bokehPass as unknown as { uniforms: BU }).uniforms;

  // GTAOPass exposes extra parameters through a getter/setter; cast to any
  const gtao = gtaoPass as unknown as Record<string, any>;
  const gtaoP = (gtao['gtaoParameters'] ?? {}) as Record<string, number | boolean>;

  // ── Panel shell ───────────────────────────────────────────────────────────

  const panel = document.createElement('div');
  panel.className = 'dbg-panel dbg-panel--collapsed';

  // Header
  const header = document.createElement('div');
  header.className = 'dbg-header';
  header.textContent = '⚙ Debug';

  const collapseBtn = document.createElement('button');
  collapseBtn.className = 'dbg-collapse-btn';
  collapseBtn.textContent = '+';
  collapseBtn.title = 'Collapse / expand';
  collapseBtn.addEventListener('click', () => {
    const collapsed = panel.classList.toggle('dbg-panel--collapsed');
    collapseBtn.textContent = collapsed ? '+' : '−';
  });
  header.appendChild(collapseBtn);
  panel.appendChild(header);
  makeDraggable(panel, header);

  const body = document.createElement('div');
  body.className = 'dbg-body';
  panel.appendChild(body);

  // ── Camera / Hotspot Info ─────────────────────────────────────────────────

  const camPos = makeInfoRow('Cam Pos');
  const camTgt = makeInfoRow('Target');
  const hotNote = makeNote('Use "Target" as hotspot origin. Offset is relative to target — edit offset[] in furniture.ts.');

  body.appendChild(makeSection('Camera & Hotspots', [
    camPos.el,
    camTgt.el,
    makeSeparator(),
    hotNote,
  ]));

  // ── Depth of Field ────────────────────────────────────────────────────────

  const dofFocus   = bokehU()['focus']?.value   ?? 5;
  const dofAp      = bokehU()['aperture']?.value ?? 0;
  const dofMaxBlur = bokehU()['maxblur']?.value  ?? 0.025;

  body.appendChild(makeSection('Depth of Field', [
    makeNote('Active only in focal mode (double-click/tap).'),
    makeSlider('Focus',    0, 20,   0.1,   dofFocus,   (v) => { bokehU()['focus'].value    = v; }),
    makeSlider('Aperture', 0, 0.1,  0.001, dofAp,      (v) => { bokehU()['aperture'].value = v; }),
    makeSlider('MaxBlur',  0, 0.05, 0.001, dofMaxBlur, (v) => { bokehU()['maxblur'].value  = v; }),
  ]));

  // ── Ambient Occlusion ─────────────────────────────────────────────────────

  const aoChildren: HTMLElement[] = [
    makeSlider('Blend', 0, 3, 0.05, gtaoPass.blendIntensity, (v) => {
      gtaoPass.blendIntensity = v;
    }),
  ];

  if (gtaoP['radius']           !== undefined) {
    aoChildren.push(makeSlider('Radius',      0.01, 2,   0.01, gtaoP['radius']           as number, (v) => { gtao['gtaoParameters'] = { ...gtao['gtaoParameters'], radius: v };           }));
  }
  if (gtaoP['distanceFallOff']  !== undefined) {
    aoChildren.push(makeSlider('Dist FallOff',0,    1,   0.01, gtaoP['distanceFallOff']  as number, (v) => { gtao['gtaoParameters'] = { ...gtao['gtaoParameters'], distanceFallOff: v };  }));
  }
  if (gtaoP['scale']            !== undefined) {
    aoChildren.push(makeSlider('Scale',       0.1,  3,   0.05, gtaoP['scale']            as number, (v) => { gtao['gtaoParameters'] = { ...gtao['gtaoParameters'], scale: v };            }));
  }
  if (gtaoP['bias']             !== undefined) {
    aoChildren.push(makeSlider('Bias',        0,    0.1, 0.001,gtaoP['bias']             as number, (v) => { gtao['gtaoParameters'] = { ...gtao['gtaoParameters'], bias: v };             }));
  }
  if (gtaoP['rings']            !== undefined) {
    aoChildren.push(makeSlider('Rings',       1,    8,   1,    gtaoP['rings']            as number, (v) => { gtao['gtaoParameters'] = { ...gtao['gtaoParameters'], rings:   Math.round(v) }; }));
  }
  if (gtaoP['samples']          !== undefined) {
    aoChildren.push(makeSlider('Samples',     4,    32,  1,    gtaoP['samples']          as number, (v) => { gtao['gtaoParameters'] = { ...gtao['gtaoParameters'], samples: Math.round(v) }; }));
  }

  aoChildren.push(makeSelect('Output', [
    { label: 'Default', value: 0 },
    { label: 'Diffuse', value: 1 },
    { label: 'AO',      value: 2 },
    { label: 'Denoise', value: 3 },
    { label: 'Depth',   value: 4 },
    { label: 'Normal',  value: 5 },
  ], gtao['output'] ?? 0, (v) => { gtao['output'] = parseInt(v, 10); }));

  body.appendChild(makeSection('Ambient Occlusion (GTAO)', aoChildren));

  // ── Anti-Aliasing ─────────────────────────────────────────────────────────

  body.appendChild(makeSection('Anti-Aliasing', [
    makeNote('Changing mode here also updates the right-panel buttons.'),
    makeSelect('Mode', [
      { label: 'Off',  value: 'off'  },
      { label: 'SMAA', value: 'smaa' },
      { label: 'SMAA + TAA', value: 'tsaa' },
    ], getAAMode(), (v) => setAAMode(v as 'off' | 'smaa' | 'tsaa')),
    makeSlider('TAA Level', 0, 4, 1, taaPass.sampleLevel, (v) => {
      taaPass.sampleLevel = Math.round(v);
    }),
  ]));

  // ── Tone Mapping ──────────────────────────────────────────────────────────

  // THREE.NeutralToneMapping was added in r165 — guard against older builds
  const tmOptions: Array<{ label: string; value: number }> = [
    { label: 'None',        value: THREE.NoToneMapping         },
    { label: 'Linear',      value: THREE.LinearToneMapping      },
    { label: 'Reinhard',    value: THREE.ReinhardToneMapping    },
    { label: 'Cineon',      value: THREE.CineonToneMapping      },
    { label: 'ACES Filmic', value: THREE.ACESFilmicToneMapping  },
    { label: 'AgX',         value: THREE.AgXToneMapping         },
  ];
  const neutralTM = (THREE as unknown as Record<string, number | undefined>)['NeutralToneMapping'];
  if (neutralTM !== undefined) tmOptions.push({ label: 'Neutral', value: neutralTM });

  body.appendChild(makeSection('Tone Mapping', [
    makeSelect('Type', tmOptions, renderer.toneMapping, (v) => {
      renderer.toneMapping = parseInt(v, 10) as THREE.ToneMapping;
    }),
    makeSlider('Exposure', 0, 4, 0.05, renderer.toneMappingExposure, (v) => {
      renderer.toneMappingExposure = v;
    }),
  ]));

  // ── Environment / HDRI ────────────────────────────────────────────────────

  const bgIntensity = (scene as unknown as Record<string, number>)['backgroundIntensity'] ?? 1;

  body.appendChild(makeSection('Environment (HDRI)', [
    makeSlider('Env Intensity', 0, 5, 0.05, scene.environmentIntensity ?? 1, (v) => {
      scene.environmentIntensity = v;
    }),
    makeSlider('BG Intensity',  0, 3, 0.05, bgIntensity, (v) => {
      (scene as unknown as Record<string, number>)['backgroundIntensity'] = v;
    }),
  ]));

  // ── Append & return ───────────────────────────────────────────────────────

  container.appendChild(panel);

  const p3 = (v: number): string => v.toFixed(3);

  const update = (): void => {
    const { x: px, y: py, z: pz } = camera.position;
    const { x: tx, y: ty, z: tz } = controls.target;
    camPos.setValue(`${p3(px)}, ${p3(py)}, ${p3(pz)}`);
    camTgt.setValue(`${p3(tx)}, ${p3(ty)}, ${p3(tz)}`);
  };

  return { element: panel, update };
}
